import React, { useEffect, useMemo, useState, useCallback } from "react";
import { config } from "@triare/auth-redux";
import dayjs from "dayjs";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import { Button, Col, Row, Typography, Spin, message } from "antd";
import {
  DownloadOutlined,
  ExclamationCircleFilled,
  HomeOutlined,
} from "@ant-design/icons";
import {
  useResponseGetById,
  useResponseUpdate,
  ResponseUpdateBody,
  ResponseItemUpdate,
  useFileUpdate,
  FileMetadataBody,
  useResponseDownload,
} from "../../../../hooks/api/responses";
import { NavLink, useParams } from "react-router";
import { dateTimeFormatRevert } from "../../../../contstant";
import isEqual from "lodash/isEqual";

import styles from "./index.module.scss";
import { SurveyPreview } from "../../../Common/SurveyPreview";
import { mapResponseToAnswers } from "../../../../utils/mapQuestionValue";
import { transformApiSurveyToApp } from "../../../../utils/transformSurveyToApi";
import LoggerCollapse from "../EditLogs";
import {
  QuestionData,
  SurveyItem,
  QuestionType,
} from "../../../../types/survey";
import { useUnsavedChangesGuard } from "../../../../utils/useUnsavedChangesGuard";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import { downloadFromAnchor } from "../../../../utils";

const { Text } = Typography;

const { displayName } = config;

const VALIDATABLE_TYPES: QuestionType[] = ["text", "date", "time"];

interface ValidationError {
  questionId: string;
  title: string;
  type: QuestionType;
}

export default function SurveyResponsesEditContent(): React.ReactNode | null {
  const { id } = useParams();
  const { open, contextHolder } = useSimpleModal();
  const { setBreadcrumbs } = useBreadcrumbs();
  const responseGetById = useResponseGetById(id);
  const responseUpdate = useResponseUpdate(id);
  const fileUpdate = useFileUpdate();
  const responseDownload = useResponseDownload();
  const [currentAnswers, setCurrentAnswers] = useState<Record<string, any>>({});
  const [initialAnswers, setInitialAnswers] = useState<Record<string, any>>({});
  const [pendingFileMetadata, setPendingFileMetadata] = useState<
    Record<string, Partial<FileMetadataBody>>
  >({});
  const [initialFileMetadata, setInitialFileMetadata] = useState<
    Record<string, Partial<FileMetadataBody>>
  >({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Responses", href: "/survey-responses" },
      { title: "Edit" },
    ]);
  }, []);
  const { answers, surveyData, originalData, rawData } = useMemo(() => {
    const data = responseGetById.data;
    if (!data) {
      return {
        answers: {},
        surveyData: null,
        originalData: null,
        rawData: null,
      };
    }
    const mappedAnswers = mapResponseToAnswers(data);

    return {
      answers: mappedAnswers,
      surveyData: transformApiSurveyToApp(data),
      originalData: data,
      rawData: data,
    };
  }, [responseGetById.data]);

  useEffect(() => {
    if (!rawData || !surveyData) return;

    setCurrentAnswers(answers);
    setInitialAnswers(answers);

    const extractedMetadata: Record<string, Partial<FileMetadataBody>> = {};

    const extractFileMetadata = (question: any) => {
      if (question.type === "image" && question.files?.length) {
        question.files.forEach((file: any) => {
          if (file.id) {
            extractedMetadata[file.id] = {
              note: file.note || "",
              tags: file.tags || [],
            };
          }
        });
      }
    };

    const items = rawData.items;
    if (Array.isArray(items)) {
      items.forEach((item: any) => {
        if (item.question) extractFileMetadata(item.question);
        if (item.section?.questions) {
          item.section.questions.forEach(extractFileMetadata);
        }
      });
    } else if (
      items &&
      typeof items === "object" &&
      Object.keys(items).length > 0
    ) {
      Object.values(items).forEach((item: any) => {
        if (item.question) extractFileMetadata(item.question);
        if (item.section?.questions) {
          item.section.questions.forEach(extractFileMetadata);
        }
      });
    }

    setPendingFileMetadata(extractedMetadata);
    setInitialFileMetadata(extractedMetadata);
    setIsInitialized(true);
  }, [rawData, surveyData, answers]);

  const normalizeForComparison = useCallback(
    (answers: Record<string, any>): Record<string, any> => {
      if (!surveyData?.items) return answers;

      const normalized: Record<string, any> = {};
      const processQuestion = (q: QuestionData) => {
        const value = answers[q.id];

        if (q.type === "image" && Array.isArray(value)) {
          normalized[q.id] = value
            .map((v: any) => (typeof v === "string" ? v : v.uid || v.id))
            .sort();
        } else {
          normalized[q.id] = value;
        }
      };

      surveyData.items.forEach((item: SurveyItem) => {
        if (item.question) processQuestion(item.question);
        if (item.section?.questions) {
          item.section.questions.forEach(processQuestion);
        }
      });

      Object.keys(answers).forEach((key) => {
        if (!(key in normalized)) {
          normalized[key] = answers[key];
        }
      });

      return normalized;
    },
    [surveyData],
  );

  const isDirty = useMemo(() => {
    if (!isInitialized) return false;
    const normalizedInitial = normalizeForComparison(initialAnswers);
    const normalizedCurrent = normalizeForComparison(currentAnswers);

    const answersChanged = !isEqual(normalizedInitial, normalizedCurrent);
    const metadataChanged = !isEqual(pendingFileMetadata, initialFileMetadata);

    return answersChanged || metadataChanged;
  }, [
    currentAnswers,
    initialAnswers,
    pendingFileMetadata,
    initialFileMetadata,
    normalizeForComparison,
    isInitialized,
  ]);

  useUnsavedChangesGuard(isDirty, () => { }, {
    open: (config) =>
      open({
        ...config,
        okButtonProps: { danger: true },
        icon: <ExclamationCircleFilled />,
      }),
  });

  const handleFileMetadataChange = useCallback(
    (fileId: string, metadata: Partial<FileMetadataBody>) => {
      setPendingFileMetadata((prev) => ({
        ...prev,
        [fileId]: { ...prev[fileId], ...metadata },
      }));
    },
    [],
  );

  useEffect(() => {
    document.title = `${displayName}: Survey Edit`;
  }, []);

  const handleAnswersChange = useCallback((newAnswers: Record<string, any>) => {
    setCurrentAnswers(newAnswers);
  }, []);

  const getAllValidatableQuestions = useCallback((): Array<{
    id: string;
    type: QuestionType;
    title: string;
  }> => {
    if (!surveyData?.items) return [];

    const questions: Array<{
      id: string;
      type: QuestionType;
      title: string;
    }> = [];

    const processQuestion = (q: QuestionData) => {
      if (!q?.id) return;

      if (!VALIDATABLE_TYPES.includes(q.type as QuestionType)) return;

      questions.push({
        id: q.id,
        type: q.type as QuestionType,
        title: q.title,
      });
    };

    surveyData.items.forEach((item: SurveyItem) => {
      if (item.question) {
        processQuestion(item.question);
      }
      if (item.section?.questions) {
        item.section.questions.forEach(processQuestion);
      }
    });

    return questions;
  }, [surveyData]);

  const validateRequiredFields = useCallback((): ValidationError[] => {
    const questions = getAllValidatableQuestions();
    const errors: ValidationError[] = [];

    questions.forEach((question) => {
      const value = currentAnswers[question.id];
      let isEmpty = false;

      if (value === null || value === undefined) {
        isEmpty = true;
      } else if (typeof value === "string" && value.trim() === "") {
        isEmpty = true;
      } else if (typeof value === "number" && isNaN(value)) {
        isEmpty = true;
      } else if (dayjs.isDayjs(value) && !value.isValid()) {
        isEmpty = true;
      }

      if (isEmpty) {
        errors.push({
          questionId: question.id,
          title: question.title,
          type: question.type,
        });
      }
    });

    return errors;
  }, [currentAnswers, getAllValidatableQuestions]);

  const transformAnswersToApi = useCallback(
    (answers: Record<string, any>, originalData: any): ResponseUpdateBody => {
      const items: ResponseItemUpdate[] = [];

      originalData?.items?.forEach((item: any) => {
        const updatedItem: ResponseItemUpdate = {
          id: item.id,
          sort: item.sort,
        };

        const mapValueToApi = (
          question: any,
          answerValue: any,
        ): Array<{ id: string; data: any }> => {
          if (
            answerValue === undefined ||
            answerValue === null ||
            answerValue === ""
          ) {
            return [];
          }

          switch (question.type) {
            case "multiSelect": {
              if (!Array.isArray(answerValue) || answerValue.length === 0)
                return [];

              return answerValue.map((val: any) => {
                if (typeof val === "object" && val.isCustom) {
                  return { id: val.id, data: val.text };
                }

                const answer = question.answers?.find((a: any) => a.id === val);
                if (answer) {
                  return { id: answer.id, data: true };
                }

                return { id: val, data: true };
              });
            }

            case "singleSelect": {
              if (!answerValue) return [];
              if (typeof answerValue === "object" && answerValue.isCustom) {
                return [{ id: answerValue.id, data: answerValue.text }];
              }

              if (typeof answerValue === "string") {
                const answer = question.answers?.find(
                  (a: any) => a.id === answerValue,
                );
                if (answer) {
                  return [{ id: answerValue, data: true }];
                }

                const answerByTitle = question.answers?.find(
                  (a: any) => a.title === answerValue,
                );
                if (answerByTitle) {
                  return [{ id: answerByTitle.id, data: true }];
                }
              }

              return [{ id: question.id, data: answerValue }];
            }
            case "toggle": {
              if (!answerValue) return [];

              const answer = question.answers?.find(
                (a: any) => a.id === answerValue,
              );
              if (answer) {
                return [{ id: answer.id, data: true }];
              }

              return [
                {
                  id: question.id,
                  data: answerValue === "yes" || answerValue === true,
                },
              ];
            }
            case "text":
            case "number":
            case "date":
            case "time":
              return [{ id: question.id, data: answerValue }];

            default:
              return [{ id: question.id, data: answerValue }];
          }
        };

        if (item.question) {
          const answerValue = answers[item.question.id];
          const { files, ...questionWithoutFiles } = item.question;

          if (item.question.type === "image" && answerValue) {
            const fileIds = Array.isArray(answerValue)
              ? answerValue.filter((id: any) => typeof id === "string")
              : [];

            updatedItem.question = {
              ...questionWithoutFiles,
              fileIds,
              value: [],
            };
          } else {
            updatedItem.question = {
              ...questionWithoutFiles,
              value: mapValueToApi(item.question, answerValue),
            };
          }
        }

        if (item.section) {
          updatedItem.section = {
            ...item.section,
            questions: item.section.questions?.map((q: any) => {
              const answerValue = answers[q.id];
              const { files, ...questionWithoutFiles } = q;

              if (q.type === "image" && answerValue) {
                const fileIds = Array.isArray(answerValue)
                  ? answerValue.filter((id: any) => typeof id === "string")
                  : [];

                return {
                  ...questionWithoutFiles,
                  fileIds,
                  value: [],
                };
              }

              return {
                ...questionWithoutFiles,
                value: mapValueToApi(q, answerValue),
              };
            }),
          };
        }

        items.push(updatedItem);
      });

      return {
        items,
        title: originalData?.title,
        status: originalData?.status,
      };
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!id || !originalData) return;

    const validationErrors = validateRequiredFields();
    if (validationErrors.length > 0) {
      return;
    }

    try {
      const fileUpdatePromises = Object.entries(pendingFileMetadata).map(
        ([fileId, metadata]) => fileUpdate.fetch(metadata, fileId),
      );

      await Promise.all(fileUpdatePromises);
      const payload = transformAnswersToApi(currentAnswers, originalData);
      await responseUpdate.fetch(payload, id);

      setInitialAnswers(currentAnswers);
      setInitialFileMetadata({});
      setPendingFileMetadata({});

      message.success("Response saved successfully");
    } catch (error) {
      message.error("Failed to save response");
      console.error(error);
    }
  }, [
    currentAnswers,
    originalData,
    pendingFileMetadata,
    fileUpdate,
    responseUpdate,
    id,
    transformAnswersToApi,
    validateRequiredFields,
  ]);

  if (responseGetById.loading) {
    return (
      <SinglePageContainer title="Edit Response" className="pagePaddingResp">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin />
        </div>
      </SinglePageContainer>
    );
  }

  if (responseGetById.error || !responseGetById.data) {
    return (
      <SinglePageContainer title="Edit Response" className="pagePaddingResp">
        <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
          Failed to load response data
        </div>
      </SinglePageContainer>
    );
  }
  const data = responseGetById.data;

  return (
    <SinglePageContainer
      title="Edit Response"
      className="pagePaddingResp"
      extra={
        <>
          <Button
            type="primary"
            onClick={handleSave}
            loading={responseUpdate.loading}
          >
            Save
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={responseDownload.loading}
            onClick={() => {
              responseDownload
                .fetch(
                  undefined,
                  `${responseGetById.data?.job?.id}/survey-response/pdf`,
                )
                .then((response) => {
                  if (!response) return;
                  downloadFromAnchor(
                    response,
                    `${responseGetById.data?.title}-${responseGetById.data?.jobId}`,
                    "application/pdf",
                  );
                });
            }}
          />
        </>
      }
      style={{ backgroundColor: "transparent" }}
    >
      <div className={styles.editInfo}>
        <Row gutter={[16, 0]}>
          <Col span={6}>
            <Text strong>Order ID</Text>
          </Col>
          <Col span={18}>
            <Text>{data?.jobId || "-"}</Text>
          </Col>

          <Col span={6}>
            <Text strong>Survey title</Text>
          </Col>
          <Col span={18}>
            <Text>{data?.title}</Text>
          </Col>

          <Col span={6}>
            <Text strong>Field technician</Text>
          </Col>
          <Col span={18}>
            <NavLink
              to={`/users/${data?.user?.id}/edit`}
              style={{ textDecoration: "underline" }}
            >
              {data?.user?.firstName} {data?.user?.lastName}
            </NavLink>
          </Col>

          <Col span={6}>
            <Text strong>Submitted at</Text>
          </Col>
          <Col span={18}>
            <Text>
              {data.submittedDate
                ? dayjs
                  .utc(data.submittedDate)
                  .add(Number(data.submittedTimezone), "hour")
                  .format(dateTimeFormatRevert)
                : "-"}
            </Text>
          </Col>
        </Row>
      </div>
      <LoggerCollapse entityId={data.id} />
      {surveyData && (
        <SurveyPreview
          survey={surveyData}
          initialAnswers={answers}
          isResponse={true}
          onAnswersChange={handleAnswersChange}
          readOnly={false}
          onFileMetadataChange={handleFileMetadataChange}
          pendingFileMetadata={pendingFileMetadata}
        />
      )}
      {contextHolder}
    </SinglePageContainer>
  );
}
